import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as R from 'remeda';
import { Observable, of } from 'rxjs';
import { catchError, map, mapTo, switchMap, take, tap } from 'rxjs/operators';
import { environment } from '~environments/environment';
import { FracMineRoute } from '~lmo/models/frac.model';
import { Mine, MineWithClaws } from '~lmo/models/mine.model';
import { ErrorHandlingService } from '~services/error-handling.service';
import { LmoFracsService } from './lmo-fracs.service';

@Injectable({
  providedIn: 'root',
})
export class LmoFracMineService {
  private currentFracId$: Observable<number>;

  constructor(
    private fracService: LmoFracsService,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private errorService: ErrorHandlingService,
  ) {
    this.currentFracId$ = this.fracService.currentFrac$.pipe(
      map((currentFrac) => (currentFrac ? currentFrac.id : null)),
    );
  }

  public getFracMinesForMesh$(meshId: number): Observable<MineWithClaws[]> {
    return this.currentFracId$.pipe(
      take(1),
      switchMap((fracId) => {
        if (!fracId) {
          return of([]);
        }
        return this.http
          .get<MineWithClaws[]>(`${environment.api}/frac/${fracId}/mesh/${meshId}/in_network_mines`)
          .pipe(map((mines) => R.sort(mines, (a, b) => a.site.name.localeCompare(b.site.name))));
      }),
    );
  }

  public getFracMines$(): Observable<Mine[]> {
    return this.currentFracId$.pipe(
      take(1),
      switchMap((fracId) => {
        if (!fracId) {
          return of([]);
        }
        return this.http
          .get<Mine[]>(`${environment.api}/frac/${fracId}/in_network_mines`)
          .pipe(map((mines) => R.sort(mines, (a, b) => a.site.name.localeCompare(b.site.name))));
      }),
    );
  }

  public getFracMineRoutes$(mineId: number): Observable<FracMineRoute> {
    return this.currentFracId$.pipe(
      take(1),
      switchMap((fracId) => {
        if (!fracId) {
          return of(null);
        }
        return this.http.get<FracMineRoute>(`${environment.api}/frac/${fracId}/mine/${mineId}/frac_mine`);
      }),
    );
  }

  public setFracMineRoute$(
    mineId: number,
    billableMileage: number,
    defaultLoadWeightPounds: number,
    applyToCompletedOrders: boolean,
    purchaseOrderID: number,
    fracClusterId?: number,
  ): Observable<boolean> {
    return this.currentFracId$.pipe(
      take(1),
      switchMap((fracId) => {
        if (!fracId) {
          return of(false);
        }
        return this.http
          .post<null>(`${environment.api}/frac_mine`, {
            applyToCompletedOrders,
            billableMileage,
            defaultLoadWeightPounds,
            fracClusterId,
            fracId,
            mineId,
            purchaseOrderID,
            setDefaultLoadWeightPounds: true,
          })
          .pipe(
            mapTo(true),
            catchError(() => of(false)),
          );
      }),
    );
  }

  public updateFracMineDirections$(fracMines): Observable<boolean> {
    return this.currentFracId$.pipe(
      take(1),
      switchMap((fracId) => {
        if (!fracId) {
          return of(false);
        }
        return this.http
          .post<null>(`${environment.api}/frac/${fracId}/frac_mine_directions`, { fracMines })
          .pipe(
            tap(() => {
              this.snackBar.open('Loader directions updated successfully', null, { duration: 5000 });
            }),
            mapTo(true),
            catchError(() => {
              this.errorService.showError('Something went wrong');
              return of(false);
            }),
          );
      }),
    );
  }

  public setFracMineRouteFromPODetail$(
    mineId: number,
    fracId: number,
    billableMileage: number,
    defaultLoadWeightPounds: number,
  ): Observable<boolean> {
    return this.http
      .post<null>(`${environment.api}/frac_mine`, {
        billableMileage,
        defaultLoadWeightPounds,
        fracId,
        mineId,
        setDefaultLoadWeightPounds: true,
      })
      .pipe(
        mapTo(true),
        catchError(() => of(false)),
      );
  }

  public getOutOfNetworkLoaders$(): Observable<Mine[]> {
    return this.currentFracId$.pipe(
      take(1),
      switchMap((fracId) => {
        if (!fracId) {
          return of([]);
        }
        return this.http.get<Mine[]>(`${environment.api}/frac/${fracId}/out_of_network_mines`);
      }),
    );
  }

  public addContractMine$(mineId: number): Observable<boolean> {
    return this.http.post<null>(`${environment.api}/account/network_loader/${mineId}`, {}).pipe(
      tap(() => {
        this.snackBar.open('Mine added to contracted list', null, { duration: 5000 });
      }),
      mapTo(true),
      catchError((error) => {
        this.errorService.showError(error);
        return of(false);
      }),
    );
  }

  public removeInNetworkLoader$(mineId: number): Observable<boolean> {
    return this.http.delete<null>(`${environment.api}/account/network_loader/${mineId}`).pipe(
      tap(() => {
        this.snackBar.open('Mine removed from network', null, { duration: 5000 });
      }),
      mapTo(true),
      catchError((error) => {
        this.errorService.showError(error);
        return of(false);
      }),
    );
  }
}
